// Main-thread facade over the AudioWorkletNode. The app calls these typed methods;
// they postMessage to the processor. Envelope snapshots arrive via the port and are
// cached for per-frame reads by the visuals layer.
import type { Mode } from '@shared/diatom';
import type { QualityTier } from '@shared/voice';
import { ENGINE_BUDGETS } from '@shared/voice';
// `?worker&url` makes Vite bundle the processor + its imports (kernel, dsp-math, rng)
// into a single hashed ESM module and hand back its URL — loadable by addModule().
import workletUrl from './resonator-processor.ts?worker&url';

export interface EnvelopeSnapshot {
  rms: Float32Array;
  pitch: Float32Array;
  active: Uint8Array;
}

export interface ResonatorBankOptions {
  tier: QualityTier;
  seed?: number;
  /** post envelope every N render quanta (default 16 ≈ 23 Hz). */
  envFrames?: number;
}

export class ResonatorBankNode {
  readonly node: AudioWorkletNode;
  readonly maxVoices: number;
  private readonly snapshot: EnvelopeSnapshot;
  private envListeners: Array<(s: EnvelopeSnapshot) => void> = [];

  constructor(ctx: AudioContext, opts: ResonatorBankOptions) {
    const budget = ENGINE_BUDGETS[opts.tier];
    this.maxVoices = budget.maxVoices;
    this.snapshot = {
      rms: new Float32Array(budget.maxVoices),
      pitch: new Float32Array(budget.maxVoices),
      active: new Uint8Array(budget.maxVoices),
    };
    this.node = new AudioWorkletNode(ctx, 'resonator-processor', {
      numberOfInputs: 0,
      numberOfOutputs: 1,
      outputChannelCount: [2],
      processorOptions: {
        maxVoices: budget.maxVoices,
        modesCap: budget.modesCap,
        seed: opts.seed ?? 0x1234567,
      },
    });
    this.node.port.onmessage = (e: MessageEvent) => {
      const d = e.data;
      if (d?.type === 'env') {
        this.snapshot.rms.set(d.rms);
        this.snapshot.pitch.set(d.pitch);
        this.snapshot.active.set(d.active);
        for (const cb of this.envListeners) cb(this.snapshot);
      }
    };
    this.node.port.postMessage({
      type: 'config',
      maxVoices: budget.maxVoices,
      modesCap: budget.modesCap,
      envFrames: opts.envFrames ?? 16,
    });
  }

  /** Register + compile the worklet module. Call once per AudioContext after resume. */
  static async register(ctx: AudioContext): Promise<void> {
    await ctx.audioWorklet.addModule(workletUrl);
  }

  setVoiceModes(voiceId: number, modes: Mode[], pan: number): void {
    this.node.port.postMessage({ type: 'setVoiceModes', voiceId, modes, pan });
  }

  strike(voiceId: number, energy: number): void {
    this.node.port.postMessage({ type: 'strike', voiceId, energy });
  }

  /** Strike a voice with a granular excitation (a grain from the active sound bank). */
  strikeGrain(voiceId: number, energy: number): void {
    this.node.port.postMessage({ type: 'strikeGrain', voiceId, energy });
  }

  /**
   * Register a recording buffer for granular excitation. The buffer is transferred
   * (zero-copy) to the audio thread, so the caller must not reuse it afterward.
   */
  loadExcitation(id: number, buffer: Float32Array): void {
    this.node.port.postMessage({ type: 'loadExcitation', id, buffer }, [buffer.buffer]);
  }

  /** Select the active grain bank (an id passed to loadExcitation), or null for none. */
  setExcitationBank(id: number | null): void {
    this.node.port.postMessage({ type: 'setExcitationBank', id });
  }

  /** Live-tune granular excitation params (ear-tuning UI). */
  setGrainParams(p: { targetRms?: number; grainMs?: number; cloudGain?: number; cloudMs?: number }): void {
    this.node.port.postMessage({ type: 'setGrainParams', ...p });
  }

  /** Begin/continue bowing a voice (sustained excitation at `level`). */
  bow(voiceId: number, level: number): void {
    this.node.port.postMessage({ type: 'bow', voiceId, level });
  }

  /** Release a bowed voice (its sustain fades, then the ring decays). */
  release(voiceId: number): void {
    this.node.port.postMessage({ type: 'release', voiceId });
  }

  /** Latest cached per-voice envelope snapshot (read each frame from rAF). */
  readEnvelopes(): EnvelopeSnapshot {
    return this.snapshot;
  }

  onEnvelope(cb: (s: EnvelopeSnapshot) => void): void {
    this.envListeners.push(cb);
  }

  connect(dest: AudioNode): void {
    this.node.connect(dest);
  }

  dispose(): void {
    this.envListeners = [];
    this.node.port.onmessage = null;
    this.node.disconnect();
  }
}
